import { useLocalStorage } from "@mantine/hooks";
import { LocalStorageKeys } from "../../../shared/enums/LocalStorageKeys";
import { CitationIndex } from "../../sources/models/Citation";
import { useMemo } from "react";
import { IconX } from "@tabler/icons-react";
import { SidebarDocumentCitation } from "./sources-sidebar/SidebarDocumentCitation";
import { CitationSourceType } from "../../../shared/enums/CitationSourceType";
import { SidebarNewsCitation } from "./sources-sidebar/SidebarNewsCitation";
import { SidebarEstimateCitation } from "./sources-sidebar/SidebarEstimateCitation";
import { SidebarUserContentCitation } from "./sources-sidebar/SidebarUserContentCitation";
import { SidebarWebCitation } from "./sources-sidebar/SidebarWebCitation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CitationOverviewProps {
  citation: CitationIndex;
  close: () => void;
  chatId: string;
  setWiderSidebar: (isWider: boolean) => void;
}

export function CitationOverview({
  citation,
  close,
  chatId,
  setWiderSidebar,
}: CitationOverviewProps) {
  const [, , clearSidebarCitation] = useLocalStorage<CitationIndex>({
    key: LocalStorageKeys.SidebarCitation,
  });
  const memoizedCitation = useMemo(() => citation, [citation]);

  const handleClose = () => {
    clearSidebarCitation();
    close();
  };

  function selectCitationComponent() {
    switch (memoizedCitation.sourceType) {
      case CitationSourceType.News:
        return <SidebarNewsCitation citation={memoizedCitation} />;
      case CitationSourceType.Filing:
      case CitationSourceType.Transcript:
        return (
          <SidebarDocumentCitation
            citation={memoizedCitation}
            setWiderSidebar={setWiderSidebar}
          />
        );
      case CitationSourceType.Estimates:
        return <SidebarEstimateCitation citation={memoizedCitation} />;
      case CitationSourceType.UserDocument:
        return (
          <SidebarUserContentCitation
            citation={memoizedCitation}
            chatId={chatId}
            setWiderSidebar={setWiderSidebar}
          />
        );
      case CitationSourceType.Internet:
        return (
          <SidebarWebCitation citation={memoizedCitation} chatId={chatId} />
        );
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between mb-1 pt-3">
        <div
          className={cn(
            "min-w-[12px] p-[2px] mr-3 text-xs font-medium leading-[135%]",
            "inline-flex justify-center items-center rounded-[2px]",
            "bg-primary/10",
          )}
        >
          {memoizedCitation.index + 1}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={handleClose}
        >
          <IconX className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex-grow overflow-hidden">
        {selectCitationComponent()}
      </div>
    </div>
  );
}
